<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import {
  iSpinner,
  iBadge,
  iIcon,
  iBadgeColours,
  iCheckbox,
  iSelect,
  iSearchInput,
  iButton,
  iFilter,
  iAmountRangeFilter,
} from '@inscribe/signal';
import { toCurrency } from '@/utils/toCurrency';
import { getAndSyncQueryParams, updateQueryParams } from '@/utils/queryParams';
import InfoTooltip from '@/shared/components/InfoTooltip';
import TablePagination from '@/shared/components/TablePagination';
import DatesFilterWithCustomRange from '@/V2/components/filters/DatesFilterWithCustomRange';
import CreditTransactionRecategorisationMenu from '@/V2/credit/components/CreditTransactionRecategorisationMenu';
import CreditCustomSorter from '@/V2/credit/components/CreditCustomSorter';
import CreditBulkRecategorisationPopup from '@/V2/credit/components/CreditBulkRecategorisationPopup';
import {
  BUSINESS_INCOME_INSIGHTS_SLUG,
  LOAN_INSIGHTS_SLUG,
} from '@/V2/credit/granular-insights/constants';
import { normalizeText } from '@/V2/credit/granular-insights/components/components.utils';
import { $event } from '@/bus';
import { formatDateTime } from '@/utils/date';
import { getUseAllDocsQueryParameter } from '@/V2/credit/utils';

const FALLBACK_ITEM = [
  {
    id: '-',
    date: '-',
    description: null,
    amount: '-',
    category: '-',
    method: '-',
    merchant: '-',
    data_source: '-',
  },
];

const DEFAULT_PER_PAGE_OPTIONS = [{ value: '20', text: '20 per page' }];

const DEFAULT_FILTER_PARAMS = {
  size: 20,
  sort_by: 'date',
  sort_direction: 'asc',
  page: 1,
  search: '',
  start_date: '',
  end_date: '',
  data_source: '',
};

export default {
  name: 'CreditTransactions',
  components: {
    DatesFilterWithCustomRange,
    iBadge,
    iSpinner,
    iIcon,
    iCheckbox,
    iSelect,
    iSearchInput,
    iButton,
    iFilter,
    iAmountRangeFilter,
    InfoTooltip,
    TablePagination,
    CreditTransactionRecategorisationMenu,
    CreditBulkRecategorisationPopup,
    CreditCustomSorter,
  },
  props: {
    tableTitle: {
      type: String,
      default: 'Transactions',
    },
    showRowIcons: {
      type: Boolean,
      default: false,
    },
    categoryFilter: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      iBadgeColours,
      FALLBACK_ITEM,
      loading: false,
      showFilters: false,
      selectAll: false,
      totalTransactions: undefined,
      perPageOptions: [...DEFAULT_PER_PAGE_OPTIONS],
      selectedItems: [],
      filterParams: {
        ...DEFAULT_FILTER_PARAMS,
        ...getAndSyncQueryParams({
          fullPath: this.$route.fullPath,
          router: this.$router,
          store: this.$store,
        }),
      },
      selectMode: 'multi',
    };
  },
  computed: {
    ...mapState({
      credit: 'credit',
    }),
    ...mapGetters([
      'creditBulkRecategorisationEnabled',
      'creditTransactionsSearchFiltersEnabled',
    ]),

    showPagination() {
      return this.totalTransactions > this.filterParams.size && !this.loading;
    },
    hasTransactions() {
      return this.credit.transactions?.data?.length > 0;
    },
    entriesText() {
      const noun = this.totalTransactions === 1 ? 'Entry' : 'Entries';
      return this.hasTransactions ? `${this.totalTransactions} ${noun}` : '---';
    },
    columnsOrder() {
      const commonColumns = [
        {
          key: 'description',
          label: 'Description',
          thClass: 'description-column th',
          tdClass: 'description-column td',
        },
        {
          key: 'date',
          label: 'Date',
        },
        {
          key: 'category',
          label: 'Category',
        },
        {
          key: 'merchant',
          label: this.merchantLabel,
        },
        {
          key: 'amount',
          label: 'Amount',
          thClass: 'amount-header',
          tdClass: 'text-right',
        },
      ];

      if (!this.categoryFilter) {
        commonColumns.splice(4, 0, {
          key: 'data_source',
          label: 'Data Source',
        });
      }

      return commonColumns;
    },
    merchantLabel() {
      let merchantLabel = 'Merchants';
      if (this.categoryFilter === LOAN_INSIGHTS_SLUG) {
        merchantLabel = 'Loan providers';
      } else if (this.categoryFilter === BUSINESS_INCOME_INSIGHTS_SLUG) {
        merchantLabel = 'Payers';
      }
      return merchantLabel;
    },
    hasSelectedItems() {
      return this.selectedItems.length > 0;
    },
    hasSelectedFilters() {
      return !!(
        (this.filterParams.start_date && this.filterParams.end_date) ||
        this.filterParams.data_source ||
        (this.filterParams.min_amount && this.filterParams.max_amount)
      );
    },
    dataSourceOptions() {
      return [
        {
          label: 'Bank Statement',
          value: 'bank_statement',
        },
        {
          label: 'Open Banking',
          value: 'open_banking',
        },
      ];
    },
    useAllDocsQueryParameter() {
      return getUseAllDocsQueryParameter(this.filterParams);
    },
  },
  watch: {
    selectAll: {
      handler: function (value) {
        if (value) {
          this.selectAllRows();
        } else {
          this.clearSelected();
        }
      },
      immediate: true,
    },
    totalTransactions: function (newTotalTransactions, oldTotalTransactions) {
      // You can perform additional actions when totalTransactions changes if needed
      this.$nextTick(() => {
        this.perPageOptions = [];
        this.generatePerPageOptions();
      });
    },
    '$route.fullPath': {
      handler: function (newFullPath) {
        const oldUseAllDocsValue = this.params?.use_all_docs;
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
        if (oldUseAllDocsValue !== this.params?.use_all_docs) {
          this.updateAllData();
        }
      },
    },
  },
  created() {
    $event.$on('credit:refresh', () => {
      this.getCustomerTransactionsCategoriesList();
      this.updateAllData();
    });
  },
  async mounted() {
    await this.getCustomerTransactionsCategoriesList();
    await this.updateAllData();

    // Show the filters if there are any selected
    this.showSelectedFilters();
  },
  methods: {
    ...mapActions([
      'getCustomerTransactions',
      'getCustomerTransactionsExportsCsv',
      'getCustomerTransactionsCategoriesList',
    ]),
    formatDateTime,
    async updateAllData() {
      this.selectAll = false;
      await this.fetchTransactions();
    },

    // This is the main method used in this component, generally, any changes to filtering/sorting/pagination/searching will trigger a call to this method
    async fetchTransactions(page = this.filterParams.page) {
      // sync query params with filterParams
      this.filterParams = {
        ...this.filterParams,
        ...getAndSyncQueryParams({
          fullPath: this.$route.fullPath,
          router: this.$router,
          store: this.$store,
        }),
      };

      // Set the page to the page number passed in
      this.filterParams.page = page;
      // Set loading to true to show the spinner
      this.loading = true;

      // deselect all rows if selectAll is true
      if (this.selectAll) {
        this.selectAll = false;
      }

      const paramsArray = [
        `sort_by=${this.filterParams.sort_by}`,
        `sort_direction=${this.filterParams.sort_direction}`,
        `size=${this.filterParams.size}`,
        `page=${this.filterParams.page}`,
        `include_unreconciled_documents=${this.useAllDocsQueryParameter}`,
      ];

      // Add category to paramsArray if categoryFilter is present
      if (this.categoryFilter) {
        paramsArray.push(`category=${this.categoryFilter}`);

        updateQueryParams({
          fullPath: this.$route.fullPath,
          router: this.$router,
          store: this.$store,
          updates: {
            category: this.categoryFilter,
          },
        });
      }

      // Add search parameter to paramsArray if searchText is longer than 2 characters
      if (this.filterParams?.search?.length > 2) {
        paramsArray.push(`search=${this.filterParams.search}`);
      }

      if (this.filterParams.start_date && this.filterParams.end_date) {
        paramsArray.push(`start_date=${this.filterParams.start_date}`);
        paramsArray.push(`end_date=${this.filterParams.end_date}`);
      }

      if (this.filterParams.data_source) {
        paramsArray.push(`data_source=${this.filterParams.data_source}`);
      }

      if (this.filterParams.min_amount && this.filterParams.max_amount) {
        // this just converts the dollar amounts to cents for the API
        paramsArray.push(`min_amount=${this.filterParams.min_amount * 100}`);
        paramsArray.push(`max_amount=${this.filterParams.max_amount * 100}`);
      }

      // Join the paramsArray with '&' to create the final params string
      const paramsString = paramsArray.join('&');

      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          page: this.filterParams.page,
          size: this.filterParams.size,
          sort_direction: this.filterParams.sort_direction,
          sort_by: this.filterParams.sort_by,
        },
      });

      try {
        await this.getCustomerTransactions({
          customerId: this.$route.params.customer_id,
          params: paramsString,
        });
        this.totalTransactions = this.credit.transactions
          ? this.credit.transactions.pagination.total
          : null;
      } finally {
        this.loading = false;
      }
    },
    onRowSelected(items) {
      this.selectedItems = items;
    },
    toggleRowSelection(index) {
      if (this.$refs.selectableTable.isRowSelected(index)) {
        this.$refs.selectableTable.unselectRow(index);
      } else {
        this.$refs.selectableTable.selectRow(index);
      }
    },
    selectAllRows() {
      if (this.$refs.selectableTable) {
        this.$refs.selectableTable.selectAllRows();
      }
    },
    clearSelected() {
      if (this.$refs.selectableTable) {
        this.selectAll = false;
        this.$refs.selectableTable.clearSelected();
      }
    },
    viewMoreText(number) {
      return `${number} per page`;
    },
    getDisplayValue(value) {
      return value ? normalizeText(value) : this.displayEmptyPlaceholder();
    },
    getDisplayValueForMerchants(value) {
      return value || this.displayEmptyPlaceholder();
    },
    displayEmptyPlaceholder() {
      return '---';
    },
    generatePerPageOptions() {
      this.perPageOptions = [...DEFAULT_PER_PAGE_OPTIONS];
      const pageOptions = [50, 100, 200, 500, 1000];

      const includedIndexes = pageOptions.reduce((acc, option, index) => {
        if (option <= this.totalTransactions) {
          acc.push(index);
        }
        return acc;
      }, []);

      includedIndexes.push(includedIndexes[includedIndexes.length - 1] + 1);

      includedIndexes.forEach((index) => {
        const option = pageOptions[index];
        if (option) {
          this.perPageOptions.push({
            value: option.toString(),
            text: this.viewMoreText(option),
          });
        }
      });
    },
    onPerPageChange(value) {
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          size: Number(value),
        },
      });
      this.fetchTransactions(1);
    },
    onSearchInput(value) {
      this.filterParams.search = value.length > 2 ? value : '';

      // Fetch transactions only if searchText is valid or empty
      if (value.length > 2 || value.length === 0) {
        updateQueryParams({
          fullPath: this.$route.fullPath,
          router: this.$router,
          store: this.$store,
          updates: {
            search: this.filterParams.search,
          },
        });
        this.fetchTransactions(1);
      }
    },
    onSortByChange(object) {
      const { sortBy, sortDirection } = object;
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          sort_by: sortBy,
          sort_direction: sortDirection,
        },
      });

      this.fetchTransactions(1);
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    onDataSourceChange(updated) {
      if (updated.selection.length > 0) {
        this.filterParams.data_source = updated.selection[0].value;
      } else {
        this.filterParams.data_source = '';
      }

      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          data_source: this.filterParams.data_source,
        },
      });
      this.fetchTransactions(1);
    },
    onDatesFilterChange(dateRange) {
      this.filterParams.start_date = dateRange.startDate;
      this.filterParams.end_date = dateRange.endDate;

      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          custom_range: dateRange.customRange,
        },
      });
      this.fetchTransactions(1);
    },
    onAmountFilterChange(amountRange) {
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          min_amount: amountRange.minAmount ? amountRange.minAmount : '',
          max_amount: amountRange.maxAmount ? amountRange.maxAmount : '',
        },
      });
      this.fetchTransactions(1);
    },
    getDataSourceLabelByValue(value) {
      const option = this.dataSourceOptions.find(
        (option) => option.value === value,
      );
      return option ? option.label : null;
    },
    showSelectedFilters() {
      this.showFilters = this.hasSelectedFilters;
    },
    clearFilters() {
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          start_date: '',
          end_date: '',
          data_source: '',
          min_amount: '',
          max_amount: '',
        },
      });

      this.fetchTransactions(1);
    },
    valueToCurrency: toCurrency,
  },
};
</script>

<template>
  <div id="credit-transactions" class="credit-transactions i-mt-13 i-pb-10">
    <div
      class="transactions-header d-flex align-items-center justify-content-between"
    >
      <div class="d-flex align-items-center">
        <h2 class="i-text-heading-5 i-text-neutral-120 i-m-0">
          {{ tableTitle }}
        </h2>

        <i-badge
          v-if="totalTransactions"
          class="i-ml-4"
          :colour="iBadgeColours.PRIMARY"
          :label="entriesText"
          alt-text-colour
        />
      </div>

      <div v-if="creditTransactionsSearchFiltersEnabled" class="d-flex i-gap-6">
        <i-search-input
          :filled-value="filterParams.search"
          class="credit-transactions__search-input"
          data-test-id="transaction-search-input"
          :debounce-time="500"
          input-placeholder="Search in descriptions"
          :keyboard-shortcut="true"
          @input="onSearchInput"
        ></i-search-input>

        <i-button
          text="Filters"
          subtype="outline"
          icon
          icon-symbol="medium-filter-3"
          @button-click="toggleFilters"
        />
      </div>
    </div>

    <div
      v-if="showFilters"
      class="credit-transactions__filters i-mt-6 i-py-6 d-flex justify-content-between align-items-center"
    >
      <div class="d-flex i-gap-3">
        <dates-filter-with-custom-range
          :params="filterParams"
          @dates-filter-updated="onDatesFilterChange"
        ></dates-filter-with-custom-range>

        <i-filter
          label="Data Source"
          :options="dataSourceOptions"
          :max-selectable="1"
          :searchable="false"
          :value="[getDataSourceLabelByValue(filterParams.data_source)]"
          @selection-changed="onDataSourceChange"
        />

        <i-amount-range-filter
          :amounts="{
            min: filterParams.min_amount ? filterParams.min_amount : undefined,
            max: filterParams.min_amount ? filterParams.max_amount : undefined,
          }"
          @amount-changed="onAmountFilterChange"
        />
      </div>
      <button
        v-if="hasSelectedFilters"
        class="i-button neutral outline sm light clear-filters"
        @click="clearFilters"
      >
        Clear filters
      </button>
    </div>

    <b-table
      id="transactions-table"
      ref="selectableTable"
      data-testid="transactions-table"
      class="i-table transactions-table i-mt-11"
      :fields="columnsOrder"
      :select-mode="selectMode"
      :selectable="creditBulkRecategorisationEnabled && hasTransactions"
      :show-empty="false"
      :items="hasTransactions ? credit.transactions.data : FALLBACK_ITEM"
      :busy="loading"
      :per-page="filterParams.size"
      responsive="sm"
      @row-selected="onRowSelected"
    >
      <template #head(description)="data">
        <div class="d-flex">
          <i-checkbox
            v-if="creditBulkRecategorisationEnabled"
            v-model="selectAll"
            :input-value="true"
            :disabled="!hasTransactions"
            :label="data.label"
            class="i-mr-6"
          ></i-checkbox>
        </div>
      </template>

      <template #cell(description)="data">
        <div class="description d-flex flex-row">
          <i-checkbox
            v-if="creditBulkRecategorisationEnabled"
            :value="data.rowSelected"
            class="i-mr-6"
            :disabled="!hasTransactions"
            @input="toggleRowSelection(data.index)"
          ></i-checkbox>
          <div
            v-if="showRowIcons && !creditBulkRecategorisationEnabled"
            class="description-icon i-mr-6"
          >
            <i-icon
              class="i-text-primary-110"
              symbol="medium-money-coins"
              :size="16"
            ></i-icon>
          </div>
          <div class="d-flex flex-column i-text-body-2">
            <span class="i-text-subheading-2">{{ data.item.description }}</span>
            <span class="i-text-body-2 i-text-neutral-110 capitalize">
              {{ getDisplayValue(data.item.method) }}
            </span>
          </div>
        </div>
      </template>

      <template #head(date)="data">
        <div class="d-flex justify-content-between align-items-center">
          <span>{{ data.label }}</span>
          <credit-custom-sorter
            class="i-ml-2"
            sort-by="date"
            :current-sort-direction="filterParams.sort_direction"
            :current-sort-by="filterParams.sort_by"
            @update-sorting="onSortByChange"
          />
        </div>
      </template>

      <template #cell(date)="data">
        {{
          data.item.date !== '-'
            ? formatDateTime(data.item.date, 'DD MMM YYYY')
            : data.item.date
        }}
      </template>

      <template #head(category)="data">
        <div class="d-flex justify-content-between align-items-center">
          <span>{{ data.label }}</span>
          <credit-custom-sorter
            class="i-ml-2"
            sort-by="category"
            asc-label="A-Z"
            desc-label="Z-A"
            :current-sort-direction="filterParams.sort_direction"
            :current-sort-by="filterParams.sort_by"
            @update-sorting="onSortByChange"
          />
        </div>
      </template>

      <template #cell(category)="data">
        <credit-transaction-recategorisation-menu
          :is-disabled="!hasTransactions"
          :transaction-category="data.item.category"
          :transaction-ids="[data.item.id]"
        />
      </template>

      <template #head(merchant)="data">
        <div class="d-flex justify-content-between align-items-center">
          <span>{{ data.label }}</span>
          <credit-custom-sorter
            class="i-ml-2"
            sort-by="merchant"
            asc-label="A-Z"
            desc-label="Z-A"
            :current-sort-direction="filterParams.sort_direction"
            :current-sort-by="filterParams.sort_by"
            @update-sorting="onSortByChange"
          />
        </div>
      </template>
      <template #cell(merchant)="data">
        <span>{{ getDisplayValueForMerchants(data.item.merchant) }}</span>
      </template>

      <template #head(data_source)="data">
        <div class="d-flex flex-row align-items-center">
          <span class="i-mr-4">{{ data.label }}</span>
          <info-tooltip
            icon-class="i-text-neutral-90"
            title="Data Source"
            subtitle="This refers to the source we get the data from, so it can be from a bank statement or from open banking data."
          ></info-tooltip>
        </div>
      </template>

      <template #cell(data_source)="data">
        <span class="capitalize">{{
          getDisplayValue(data.item.data_source)
        }}</span>
      </template>

      <template #head(amount)="data">
        <div class="d-flex justify-content-end align-items-center">
          <span>{{ data.label }}</span>
          <credit-custom-sorter
            class="i-ml-2"
            :menu-width="200"
            menu-align="right"
            sort-by="amount"
            asc-label="Lowest to Highest"
            desc-label="Highest to Lowest"
            :current-sort-direction="filterParams.sort_direction"
            :current-sort-by="filterParams.sort_by"
            @update-sorting="onSortByChange"
          />
        </div>
      </template>

      <template #cell(amount)="data">
        <span class="text-right text-xs-right">{{
          valueToCurrency(data.item.amount, 2)
        }}</span>
      </template>

      <template #table-busy>
        <i-spinner class="mx-auto i-my-16" size="xl"></i-spinner>
      </template>
    </b-table>

    <div
      class="d-flex align-items-center"
      :class="{
        'justify-content-between': showPagination,
        'justify-content-end': !showPagination,
      }"
    >
      <table-pagination
        v-if="showPagination"
        table-id="transactions-table"
        :total-rows="totalTransactions"
        :per-page="Number(filterParams.size)"
        :current-page="Number(filterParams.page)"
        @page-change="fetchTransactions"
      />

      <i-select
        data-testid="per-page-select"
        :options="perPageOptions"
        :default-value="filterParams.size.toString()"
        placeholder="# per page"
        @option-selected="onPerPageChange"
      ></i-select>
    </div>

    <credit-bulk-recategorisation-popup
      v-if="creditBulkRecategorisationEnabled && hasSelectedItems"
      :selected-transactions="selectedItems"
      @clear-selection="clearSelected"
    />
  </div>
</template>

<style lang="scss" scoped>
.credit-transactions {
  position: relative;

  &__search-input {
    width: 270px;
    height: 42px;
  }

  &__filters {
    border-top: 1px solid $light-neutral-50;

    .clear-filters {
      padding: 8px 12px;
      font-size: map-get($text-size-standard, 3);
      font-weight: map-get($text-weights, 'medium') !important;
      border-radius: 16px;
    }
  }
  ::v-deep .transactions-table {
    .description {
      align-items: center;

      .description-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 40px;
        width: 40px;
        height: 40px;
        background: $light-primary-30;
        border-radius: 50%;
      }
    }

    td,
    th {
      @media screen and (max-width: 800px) {
        padding: 2px;
      }

      @media screen and (max-width: 1100px) {
        padding: 16px 8px;
      }
    }

    .description-column {
      &.th {
        label {
          margin-left: 2px;
          color: $light-neutral-90 !important;
          font-size: map-get($text-size-standard, 3) !important;
          font-weight: map-get($text-weights, 'medium') !important;
        }
      }
      &.td {
        max-width: 390px;
      }
    }
    .capitalize {
      display: inline-block;

      &::first-letter {
        text-transform: uppercase;
      }
    }

    .category-badge {
      display: inline-flex !important;
    }
  }

  .i-pagination {
    margin-bottom: 0;
  }

  ::v-deep .i-select {
    width: 150px;
    .items {
      bottom: 48px !important;
    }
  }
}
</style>
